<template>
    <div>
        <div
            :class="[
                'header-with-tiles',
                { 'header-with-tiles--reduced-margins': isEmailHealthImprovementsEnabled },
            ]"
        >
            <div class="email-page-header">
                <div class="title-arrow">
                    <ds-text-button gray as="a" href="/Dashboard/userDashboard.jsp">
                        <ds-icon name="arrow-left" />
                    </ds-text-button>

                    <div>Email engagement</div>
                </div>

                <div class="header-actions">
                    <ds-dropdown
                        v-if="isEngagementDetailsLayoutUpdateEnabled"
                        class="engagement-header-dropdown"
                        position="bottom-end"
                    >
                        <template #default>
                            <ds-text-button class="engagement-header-dropdown-button" trailing-icon="chevron-down">
                                <span class="engagement-header-dropdown-label">
                                    How to improve engagement
                                </span>
                            </ds-text-button>
                        </template>

                        <template #menu>
                            <ul class="dropdown-menu-list">
                                <li
                                    v-for="item in engagementCtaItems"
                                    :key="item.id"
                                    class="dropdown-menu-item"
                                >
                                    <a
                                        :href="item.link"
                                        class="dropdown-menu-link option"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {{ item.label }}
                                    </a>
                                </li>
                            </ul>
                        </template>
                    </ds-dropdown>

                    <button type="button" class="feedback-link" @click="isFeedbackModalOpen=true">
                        <ds-icon name="message" />
                        <span>
                            Leave feedback
                        </span>
                    </button>
                </div>

                <share-feedback-modal
                    :is-open="isFeedbackModalOpen"
                    :feature="$options.EMAIL_ANALYTICS"
                    :slack-channels="[$options.SLACK_CHANNELS.IN_APP_FEEDBACK, $options.SLACK_CHANNELS.EMAIL_ANALYTICS_FEEDBACK]"
                    @close="isFeedbackModalOpen = false"
                />
            </div>

            <ds-inline-alert
                v-if="showMessage"
                leading-icon
                type="critical"
                class="alert-banner"
            >
                {{ getAlertBannerMessage }}
                <ds-inline-alert-footer-link
                    v-if="showAlertBannerLink"
                    href="https://help.infusionsoft.com/help/contact-support"
                >
                    Contact Support
                </ds-inline-alert-footer-link>
            </ds-inline-alert>

            <div class="engagement-details-page">
                <ds-card class="card engagement-overview-card">
                    <ds-card-header class="card-title">
                        <div class="email-card-header">
                            Average email engagement
                        </div>

                        <div />

                        <div class="last-30-days">
                            Last 30 days
                        </div>
                    </ds-card-header>

                    <ds-card-body>
                        <progress-bar-pi-chart-card
                            :data-by-type="dataTypesWithColors"
                            :progress-bar-value="averageEngagementDays"
                            :progress-bar-upper-limit="averageEngagementDaysUpperLimit"
                            :progress-bar-healthy-limit="averageEngagementDaysHealthyLimit"
                            :progress-bar-units="' days'"
                            :sorted-data="false"
                            :link-label="'View details'"
                            link-href="#engagement-report"
                            :empty-state-message="'No email engagement has been recorded'"
                        />
                    </ds-card-body>
                </ds-card>

                <!-- AI Insights panel shown when feature flag is enabled -->
                <div v-if="isAiEmailEngagementSummaryBetaEnabled" class="ai-insights-panel card engagement-overview-card">
                    <div class="ai-insights-inner">
                        <!-- Header with title and AI icon -->
                        <div class="ai-insights-header">
                            <div class="ai-insights-title-section">
                                <div class="ai-insights-title">
                                    {{ $t('email.healthWidget.aiInsights.title') }}
                                </div>
                                <div class="ai-insights-subtitle">
                                    <strong>{{ $t('email.healthWidget.aiInsights.lastUpdatedLabel') }}</strong><span v-if="aiLastUpdatedText" class="ai-insights-subtitle-value">{{ aiLastUpdatedText }}</span>
                                </div>
                            </div>
                            <magic-wand-icon class="ai-insights-icon" :size="24" />
                        </div>

                        <!-- Initial state -->
                        <div v-if="!aiSummaryInitiated" class="ai-summary-initial">
                            <ds-button
                                variant="outline"
                                class="generate-ai-summary-button"
                                data-qa="generate-ai-summary-button"
                                @click="initiateAISummary"
                            >
                                <template #leading>
                                    <magic-wand-icon class="generate-ai-summary-icon" :size="16" />
                                </template>
                                {{ $t('email.healthWidget.aiSummary.generateButton') }}
                            </ds-button>
                            <p class="ai-summary-description">
                                {{ $t('email.healthWidget.aiSummary.description') }}
                            </p>
                        </div>

                        <!-- Loading state -->
                        <div v-else-if="aiSummaryLoading" class="ai-insights-loading">
                            <ds-spinner :size="24" />
                            <p>{{ currentLoadingMessage }}</p>
                        </div>

                        <!-- Content state -->
                        <!-- eslint-disable vue/no-v-html -->
                        <div
                            v-else-if="aiSummary"
                            class="ai-summary-content markdown-content"
                            v-html="formattedAiSummary"
                        />
                        <!-- eslint-enable vue/no-v-html -->

                        <!-- Error state -->
                        <div v-else class="ai-summary-content">
                            <p>{{ $t('email.healthWidget.aiSummary.error') }}</p>
                        </div>
                    </div>
                </div>

                <!-- Description card shown when feature flag is disabled -->
                <description-card
                    v-else
                    class="card engagement-overview-card"
                    header-text="What is email engagement?"
                >
                    <template v-slot:content>
                        <div v-if="isEmailHealthContentUpdateEnabled">
                            <p>Email engagement refers to how recently and frequently your contacts interact with your emails — specifically, how many days have passed since they last opened or clicked an email sent from your Keap app.</p>

                            <span><strong>What does a "highly engaged" contact look like? </strong> A contact is considered highly engaged if they have:</span>
                            <br />
                            <ul class="ul">
                                <li>Opened at least one of your emails within the past 30 days, or</li>
                                <li>Clicked a link in any of your emails within the past 30 days</li>
                            </ul>

                            <p>
                                Why is engagement so important? Because inbox providers (like Gmail and Outlook) are smarter than ever. They've moved beyond just blocking obvious spam — now they're filtering out emails that don't get interaction. If your audience isn't opening or clicking, your messages could start landing in the spam folder.
                            </p>
                            <p>
                                Bottom line: consistently engaging your contacts isn't just a nice-to-have — it's a must if you want to reach the inbox.
                            </p>
                        </div>

                        <div v-else>
                            <p>Email engagement is the time (in days) between when a customer last opens/clicks an email and when you send a new email via your Keap app.</p>

                            <span><strong>A highly engaged contact:</strong></span>
                            <br />
                            <ul class="ul">
                                <li>Has opened email you've sent within 30 days</li>
                                <li>Or has clicked a link in any email you've sent within 30 days</li>
                            </ul>

                            <p>
                                Getting your customers to engage with your emails is important. Why? Because spam filters are getting smarter. They've mastered filtering out blatant spam and have now evolved to
                                <strong>filter out emails that recipients aren't engaging with. </strong>
                                Think about that: just by not engaging with your email, a recipient can land you in spam city.
                            </p>
                        </div>
                    </template>
                </description-card>

                <ds-card v-if="!isEngagementDetailsLayoutUpdateEnabled" :class="getClass()">
                    <ds-card-header class="card-title">
                        <div class="email-card-header">
                            Take action to improve email engagement
                        </div>

                        <div class="last-30-days">
                            Recommended actions
                        </div>
                    </ds-card-header>

                    <ds-card-body>
                        <engagement-take-action-checklist-card
                            :is-update-help-article-links-enabled="isUpdateHelpArticleLinksEnabled"
                            :is-unengaged-email-status-processing-enabled="isUnengagedEmailStatusProcessingEnabled"
                            :app-edition="appEdition"
                        />
                    </ds-card-body>
                </ds-card>

                <EmailRehabServiceCard
                    v-if="displayRehabServiceCard && !isEngagementDetailsLayoutUpdateEnabled"
                    class="card"
                    icon-path="/resources/svg/customer-support.svg"
                    :header-text="$t('email.healthWidget.rehabServiceTile.engagement.headerText')"
                    :body-text="$t('email.healthWidget.rehabServiceTile.engagement.bodyText')"
                    :link-address="linkAddress"
                    :help-button-label="$t('email.healthWidget.rehabServiceTile.engagement.helpButtonLabel')"
                />

                <ds-card v-if="isEmailHealthImprovementsEnabled" class="single-line-card">
                    <ds-card-header class="card-title">
                        <div class="email-card-header">
                            Engagement Report
                        </div>
                        <div class="last-30-days">
                            Last 30 days
                        </div>
                    </ds-card-header>

                    <ds-card-body class="engagement-report-card-body">
                        <engagement-report
                            id="engagement-report"
                            :timezone="timezone"
                            :is-email-health-improvements-enabled="isEmailHealthImprovementsEnabled"
                            :is-engagement-report-selectable="isEngagementReportSelectable"
                        />
                    </ds-card-body>
                </ds-card>
            </div>
        </div>
        <div v-if="!isEmailHealthImprovementsEnabled" class="engagement-report-container">
            <engagement-report
                id="engagement-report"
                :timezone="timezone"
                :is-email-health-improvements-enabled="isEmailHealthImprovementsEnabled"
            />
        </div>
    </div>
</template>

<script>
import ShareFeedbackModal from '@/components/shared/ShareFeedbackModal';
import { EMAIL_ANALYTICS } from '@/components/shared/constants/shareFeedback.constants';
import { SLACK_CHANNELS } from '@/components/util/slack/slack.constants';
import { complianceThresholds } from '@/utils/email.util';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import EngagementReport from './EngagementReport';
import EngagementTakeActionChecklistCard from './EngagementTakeActionChecklistCard';
import MagicWandIcon from './MagicWandIcon';
import ProgressBarPiChartCard from './ProgressBarPiChartCard';
import DescriptionCard from './shared/DescriptionCard';
import EmailRehabServiceCard from './shared/EmailRehabServiceCard';

const ENGAGEMENT_ITEM_COLORS = [
    '#0040D8',
    '#8BDC00',
    '#36DFFF',
    '#FFC400',
    '#F44366',
];
const ENGAGEMENT_HOVER_LABELS = [
    'Never engaged',
    '0-30 days',
    '31-60 days',
    '61-120 days',
    '>120 days',
];
const ENGAGEMENT_CTA_ITEMS_CLASSIC = [
    {
        id: 1,
        link: 'https://learn.thryv.com/hc/en-us/articles/37241726237069-Why-Domain-Authentication-is-Required-with-Keap',
        label: 'Set up domain authentication (DKIM)',
    },
    {
        id: 2,
        link: 'https://learn.thryv.com/hc/en-us/articles/41630564193805-Engagement-Essentials-Understanding-Email-Engagement-and-Why-It-Matters-with-Keap',
        label: 'Understand engagement essentials',
    },
    {
        id: 3,
        link: 'https://learn.thryv.com/hc/en-us/articles/41637399074061-Tag-Contacts-Unengaged-Up-to-30-Days-for-a-Re-engagement-Campaign-with-Keap',
        label: 'Tag unengaged contacts',
    },
    {
        id: 4,
        link: 'https://learn.thryv.com/hc/en-us/articles/37443318628621-How-to-Avoid-Content-Filters-In-Email-Marketing-with-Keap',
        label: 'Improve your email content',
    },
    {
        id: 5,
        link: 'https://learn.thryv.com/hc/en-us/articles/41638117123213-Send-a-Re-engagement-Email-to-your-Unengaged-Contacts-with-Keap',
        label: 'Start a re-engagement campaign',
    },
    {
        id: 6,
        link: 'https://learn.thryv.com/hc/en-us/articles/41630277888013-Automated-List-Management-with-Keap',
        label: 'Set up list automation',
    },
];
const ENGAGEMENT_CTA_ITEMS_KEAP = [
    {
        id: 1,
        link: 'https://learn.thryv.com/hc/en-us/articles/37241726237069-Why-Domain-Authentication-is-Required-with-Keap',
        label: 'Set up domain authentication (DKIM)',
    },
    {
        id: 2,
        link: 'https://learn.thryv.com/hc/en-us/articles/41630564193805-Engagement-Essentials-Understanding-Email-Engagement-and-Why-It-Matters-with-Keap',
        label: 'Understand engagement essentials',
    },
    {
        id: 3,
        link: 'https://learn.thryv.com/hc/en-us/articles/41637399074061-Tag-Contacts-Unengaged-Up-to-30-Days-for-a-Re-engagement-Campaign-with-Keap',
        label: 'Tag unengaged contacts',
    },
    {
        id: 4,
        link: 'https://learn.thryv.com/hc/en-us/articles/37443318628621-How-to-Avoid-Content-Filters-In-Email-Marketing-with-Keap',
        label: 'Improve your email content',
    },
    {
        id: 5,
        link: 'https://learn.thryv.com/hc/en-us/articles/41638117123213-Send-a-Re-engagement-Email-to-your-Unengaged-Contacts-with-Keap',
        label: 'Start a re-engagement campaign',
    },
];

export default {
    EMAIL_ANALYTICS,
    SLACK_CHANNELS,

    components: {
        EmailRehabServiceCard,
        DescriptionCard,
        MagicWandIcon,
        ProgressBarPiChartCard,
        EngagementReport,
        EngagementTakeActionChecklistCard,
        ShareFeedbackModal,
    },

    props: {
        appId: String,
        appEdition: String,
        firstName: String,
        lastName: String,
        timezone: String,
        phoneNumber: String,
        email: String,
        isEngagementDetailsLayoutUpdateEnabled: {
            type: Boolean,
            default: false,
        },
        isEmailHealthContentUpdateEnabled: {
            type: Boolean,
            default: false,
        },
        isEngagementReportSelectable: {
            type: Boolean,
            default: false,
        },
        isUpdateHelpArticleLinksEnabled: {
            type: Boolean,
            default: false,
        },
        isEmailHealthImprovementsEnabled: {
            type: Boolean,
            default: false,
        },
        isUnengagedEmailStatusProcessingEnabled: Boolean,
        isEmailLockedDown: {
            type: Boolean,
            default: false,
        },
        isAiEmailEngagementSummaryBetaEnabled: {
            type: Boolean,
            default: false,
        },
        isEngagementBucketsPayloadEnabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            errorLoading: false,
            averageEngagementDays: 0,
            averageEngagementDaysByType: [],
            alertBannerMessage: '',
            averageEngagementDaysUpperLimit: null,
            averageEngagementDaysHealthyLimit: null,
            isFeedbackModalOpen: false,
            aiSummary: '',
            aiSummaryLoading: false,
            aiSummaryStreaming: false,
            aiSummaryInitiated: false,
            loadingMessages: [],
            currentLoadingMessageIndex: 0,
            loadingMessageIntervalId: null,
            aiSummaryCompletedAt: null,
        };
    },

    created() {
        this.load();
    },

    beforeDestroy() {
        this.stopLoadingMessageCycle();
    },

    computed: {
        displayRehabServiceCard() {
            return this.averageEngagementDays > this.averageEngagementDaysUpperLimit;
        },

        linkAddress() {
            return `https://keap.com/email-deliverability-consultation?appId=${this.appId}&firstName=${this.firstName}&lastName=${this.lastName}&email=${this.email}&phone=${this.phoneNumber}`;
        },

        error_pulling_data () {
            return this.loading === false && this.errorLoading === true;
        },
        dataTypesWithColors() {
            return this.averageEngagementDaysByType?.map((entity, index) => {
                return {
                    type: entity.type,
                    number: entity.number,
                    color: ENGAGEMENT_ITEM_COLORS[index % ENGAGEMENT_ITEM_COLORS.length],
                    legendHoverLabel: ENGAGEMENT_HOVER_LABELS[index % ENGAGEMENT_HOVER_LABELS.length],
                };
            });
        },
        getAlertBannerMessage() {
            if (this.isEmailLockedDown) {
                return 'Due to a compliance issue your ability to send email broadcasts has been disabled. '
                        + 'Contact support for details on how to re-enable broadcast sending.';
            }

            if (this.error_pulling_data) {
                return 'An issue was encountered while retrieving your data. Please try reloading the page.';
            }

            if (this.averageEngagementDays > complianceThresholds.averageEngagementDaysHealthyLimit) {
                return 'You are about to exceed the engagement limit. '
                        + 'If you do not take action soon, you may fall out of Keap’s Email Compliance Policy and if so, email sending restrictions may occur.';
            }

            return 'Your email engagement is good.';
        },

        showAlertBannerLink() {
            return this.isEmailLockedDown;
        },

        showMessage() {
            return this.error_pulling_data
                    || this.isEmailLockedDown;
        },

        formattedAiSummary() {
            if (!this.aiSummary) {
                return '';
            }
            const rawHtml = marked(this.aiSummary);

            return DOMPurify.sanitize(rawHtml);
        },

        currentLoadingMessage() {
            if (this.loadingMessages.length === 0) {
                return '';
            }

            return this.loadingMessages[this.currentLoadingMessageIndex];
        },

        aiLastUpdatedText() {
            if (this.aiSummaryCompletedAt) {
                return this.$t('email.healthWidget.aiInsights.lastUpdated.now');
            }

            if (this.aiSummaryStreaming) {
                return this.$t('email.healthWidget.aiInsights.lastUpdated.inProgress');
            }

            return '';
        },
        engagementCtaItems() {
            const isClassic = this.appEdition === 'Keap Max Classic Edition';

            if (isClassic) {
                return ENGAGEMENT_CTA_ITEMS_CLASSIC;
            }

            return ENGAGEMENT_CTA_ITEMS_KEAP;
        },
    },

    methods: {
        getClass() {
            if (this.displayRehabServiceCard) {
                return 'card';
            }

            return 'single-line-card';
        },

        startLoadingMessageCycle() {
            this.stopLoadingMessageCycle();

            this.loadingMessages = [
                this.$t('email.healthWidget.aiInsights.loading.generatingReport'),
                this.$t('email.healthWidget.aiInsights.loading.analyzingList'),
                this.$t('email.healthWidget.aiInsights.loading.calculatingRates'),
                this.$t('email.healthWidget.aiInsights.loading.identifyingOpportunities'),
            ];
            this.currentLoadingMessageIndex = 0;

            this.loadingMessageIntervalId = setInterval(() => {
                this.currentLoadingMessageIndex = (this.currentLoadingMessageIndex + 1) % this.loadingMessages.length;
            }, 3000);
        },

        stopLoadingMessageCycle() {
            if (this.loadingMessageIntervalId) {
                clearInterval(this.loadingMessageIntervalId);
                this.loadingMessageIntervalId = null;
            }
        },

        markAiSummaryCompleted(hasReceivedSummaryText) {
            if (hasReceivedSummaryText) {
                this.aiSummaryCompletedAt = new Date();
            }
        },

        async load() {
            await this.loadMetrics();
        },

        async initiateAISummary() {
            // Track button click with Pendo
            this.trackPendoEvent('AI Email Engagement Summary Button Clicked', {
                appId: this.appId,
                appEdition: this.appEdition,
                averageEngagementDays: this.averageEngagementDays,
            });

            this.aiSummaryInitiated = true;
            this.aiSummaryCompletedAt = null;
            this.startLoadingMessageCycle();
            await this.getAISummaryStream();
        },

        /**
         * Tracks an event with Pendo analytics.
         * Pendo is loaded globally via the JSP template (pendoAnalytics.tag).
         * @param {string} eventName - The name of the event to track
         * @param {Object} eventProperties - Additional properties to include with the event
         */
        trackPendoEvent(eventName, eventProperties = {}) {
            if (window.pendo && typeof window.pendo.track === 'function') {
                window.pendo.track(eventName, eventProperties);
            }
        },

        async loadMetrics() {
            try {
                const metricsResponse = await axios.get('/app/metrics/getEngagementPageMetrics');

                this.averageEngagementDays = metricsResponse.data.emailHealthMetrics.averageEngagementDays;
                const engagementData = metricsResponse.data.emailEngagementCounts;

                if (this.isEngagementBucketsPayloadEnabled) {
                    const buckets = engagementData?.engagementBuckets ?? [];

                    const bucketLabels = [
                        this.$t('email.healthWidget.engagement.buckets.neverEngaged'),
                        this.$t('email.healthWidget.engagement.buckets.highlyEngaged'),
                        this.$t('email.healthWidget.engagement.buckets.engaged'),
                        this.$t('email.healthWidget.engagement.buckets.lowEngagement'),
                        this.$t('email.healthWidget.engagement.buckets.unengaged'),
                    ];

                    // Order matters as we are zipping this list with other lists in dataTypesWithColors()
                    this.averageEngagementDaysByType = buckets.map((bucket, index) => ({
                        type: bucketLabels[index] ?? `Bucket ${index + 1}`,
                        number: bucket.count,
                    }));
                } else {
                    // Order matters as we are zipping this list with other lists in dataTypesWithColors()
                    this.averageEngagementDaysByType = [
                        {
                            type: 'Sent to, never engaged',
                            number: engagementData.contactsNeverEngaged,
                        },
                        {
                            type: 'Highly engaged',
                            number: engagementData.contactsEngagedBetween0And30Days,
                        },
                        {
                            type: 'Engaged',
                            number: engagementData.contactsEngagedBetween31And60Days,
                        },
                        {
                            type: 'Low engagement',
                            number: engagementData.contactsEngagedBetween61And120Days,
                        },
                        {
                            type: 'Unengaged',
                            number: engagementData.contactsEngagedAbove120Days,
                        },
                    ];
                }

                this.averageEngagementDaysUpperLimit = complianceThresholds.averageEngagementDaysUpperLimit;
                this.averageEngagementDaysHealthyLimit = complianceThresholds.averageEngagementDaysHealthyLimit;
                this.loading = false;
            } catch (e) {
                this.loading = false;
                this.errorLoading = true;
            }
        },

        async getAISummaryStream() {
            try {
                this.aiSummaryLoading = true;
                this.aiSummaryStreaming = true;
                this.aiSummary = '';

                // Use fetch for streaming response (axios doesn't support ReadableStream in browsers)
                const response = await fetch('/app/aiemailhealth/analyzeEngagementStream', {
                    method: 'POST',
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let hasReceivedSummaryText = false;

                // eslint-disable-next-line no-constant-condition
                while (true) {
                    // eslint-disable-next-line no-await-in-loop
                    const { done, value } = await reader.read();

                    if (done) {
                        this.markAiSummaryCompleted(hasReceivedSummaryText);
                        break;
                    }

                    const chunk = decoder.decode(value);
                    const lines = chunk.split('\n');

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(line.slice(6));

                                if (data.error) {
                                    console.error('Streaming error:', data.error);
                                    this.aiSummary = this.$t('email.healthWidget.aiSummary.errorWithRetry');

                                    return;
                                }

                                if (data.done) {
                                    this.markAiSummaryCompleted(hasReceivedSummaryText);

                                    return;
                                }

                                if (data.text) {
                                    this.aiSummary += data.text;

                                    if (!hasReceivedSummaryText) {
                                        this.stopLoadingMessageCycle();
                                    }
                                    hasReceivedSummaryText = true;
                                    this.aiSummaryLoading = false;
                                }
                            } catch (e) {
                                console.warn('Skipping malformed JSON:', line);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error calling AI API:', error);
                this.aiSummary = this.$t('email.healthWidget.aiSummary.errorWithRetry');
            } finally {
                this.aiSummaryLoading = false;
                this.aiSummaryStreaming = false;
                this.stopLoadingMessageCycle();
            }
        },
    },
};

</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.alert-banner {
    display: flex;
}

.ul {
    list-style: disc;
}

.engagement-details-page {
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;
}

.engagement-report-container {
    margin: auto;
    width: 90vw;
}

.email-page-header {
    display: flex;
    align-items: center;
    padding-bottom: 0;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: $spacing-200;
}

.title-arrow {
    font-size: $font-size-jumbo;
    align-items: center;
    flex: 50%;
    display: flex;
}

.header-with-tiles {
    max-width: 72rem;
    margin: auto;
}

.header-with-tiles--reduced-margins {
    max-width: 94rem;
    width: calc(100% - #{$spacing-050});
}

.header-with-tiles--reduced-margins .engagement-details-page {
    justify-content: space-between;
}

.header-with-tiles--reduced-margins .card {
    margin-left: 0;
    margin-right: 0;
    width: calc(50% - #{$spacing-050});
    min-width: 0;
}

.header-with-tiles--reduced-margins .engagement-overview-card {
    min-height: px-to-rem(430);
}

.header-with-tiles--reduced-margins .single-line-card {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    min-width: 0;
    min-height: calc(100vh - #{px-to-rem(220)});
    display: flex;
    flex-direction: column;
}

.card {
    margin: $spacing-100;
    width: calc(50% - 1.5em);
}

.single-line-card {
    margin: $spacing-100;
    width: calc(100% - 1.5em);
}

.engagement-report-card-body {
    padding-top: 0;
    display: flex;
    flex: 1;
    min-width: 0;
}

.engagement-report-card-body > .engagement-report {
    width: 100%;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.email-card-header {
    flex: 50%;
    float: left;
    font-size: $font-size-lg;
    padding-top: $spacing-100;
    padding-left: $spacing-200;
}

.last-30-days {
    font-size: $font-size-xs;
    padding-top: $spacing-100;
    padding-left: $spacing-200;
    text-align: left;
}

.feedback-link {
    display: flex;
    gap: $spacing-050;
    font-size: $font-size-sm;
    align-items: center;
    color: $color-blue-700;
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
}

.engagement-header-dropdown-button {
    font-size: $font-size-sm;
}

.engagement-header-dropdown-label {
    font-size: $font-size-sm;
    font-weight: normal;
}

.engagement-header-dropdown .option {
    color: $color-ink-900;
}

.engagement-header-dropdown .dropdown-menu-link {
    display: block;
    text-decoration: none;
}

.engagement-header-dropdown .dropdown-menu-list {
    margin: 0;
    padding: 0;
}

.card-title {
    display: flex;
    flex-direction: column;
    border-bottom: solid px-to-rem(1) $color-ink-200;
    padding-bottom: $spacing-100;
}

.ai-summary-content {
    max-height: 20rem;
    overflow-y: auto;

    // Use ::v-deep because v-html content doesn't receive the scoped data attribute
    &.markdown-content ::v-deep {
        h2, h3, h4 {
            margin-top: $spacing-300;
            margin-bottom: $spacing-200;
            line-height: 1.3;
            font-weight: 600;
        }

        h2 {
            font-size: $font-size-lg;
        }

        h3 {
            font-size: $font-size-md;
        }

        h4 {
            font-size: $font-size-sm;
        }

        h2:first-child, h3:first-child, h4:first-child {
            margin-top: 0;
        }

        p {
            margin-bottom: $spacing-200;
            line-height: 1.6;
            color: rgba(0, 0, 0, 0.824) !important; // Match <li> text color; !important needed to override global p styles
        }

        p:last-child {
            margin-bottom: 0;
        }

        ul, ol {
            margin-bottom: $spacing-200;
            padding-left: $spacing-400;

            li {
                margin-bottom: $spacing-100;
            }
        }

        code {
            background-color: $color-ink-100;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: monospace;
            font-size: 0.9em;
        }

        pre {
            background-color: $color-ink-100;
            padding: $spacing-200;
            border-radius: 4px;
            overflow-x: auto;
            margin-bottom: $spacing-200;

            code {
                background-color: transparent;
                padding: 0;
            }
        }

        blockquote {
            border-left: 4px solid $color-blue-500;
            padding-left: $spacing-200;
            margin: $spacing-200 0;
            font-style: italic;
        }
    }
}

.ai-summary-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: $spacing-300 0;

    p {
        margin-top: $spacing-200;
        color: $color-ink-600;
        font-size: $font-size-sm;
    }
}

// Semi-transparent so the outer panel gradient softly bleeds through,
// matching the Figma "frosted glass" inner surface.
@mixin ai-insights-frosted-surface {
    background-color: rgba(255, 255, 255, 0.5);
    border-radius: $border-radius;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.06);
    padding: $spacing-200;
}

.ai-summary-initial {
    @include ai-insights-frosted-surface;
}

.ai-summary-description {
    margin: $spacing-200 0 0;
    color: $color-ink-800;
    font-size: $font-size-sm;
    line-height: 1.5;
}

.generate-ai-summary-icon {
    color: $color-purple-500;
}

// AI Insights Panel (Beta Design)
.ai-insights-panel {
    background:
        linear-gradient(135deg, rgba(255, 80, 0, 0.05) 0%, rgba(104, 56, 237, 0.07) 100%),
        $color-paper;
    border-radius: $border-radius;
    box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.2);
    display: flex;
    min-width: 0;
}

.ai-insights-inner {
    padding: $spacing-200;
    display: flex;
    flex-direction: column;
    flex: 1;
}

.ai-insights-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: $spacing-200;
}

.ai-insights-title-section {
    display: flex;
    flex-direction: column;
}

.ai-insights-title {
    font-size: $font-size-lg;
    font-weight: 600;
    color: $color-ink-800;
}

.ai-insights-subtitle {
    font-size: $font-size-xs;
    margin-top: $spacing-050;
    color: $color-ink-800;
    font-weight: $font-weight-regular;

    strong {
        font-weight: $font-weight-semibold;
    }
}

.ai-insights-subtitle-value {
    margin-inline-start: $spacing-050;
}

.ai-insights-icon {
    flex-shrink: 0;
    color: $color-purple-500;
}

.ai-insights-loading {
    @include ai-insights-frosted-surface;
    display: flex;
    flex-direction: row;
    align-items: center;

    p {
        margin: 0 0 0 $spacing-200;
        color: $color-ink-800;
        font-size: $font-size-sm;
    }
}

</style>
